<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Attendance Report"
                :btnTitle="'Add New'"
                :showAddNew="false"
            />
        </div>
        <div class="my-2 px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="onClickToGo"
                class="tds-vds-query-setter"
            >
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Employee</label>
                <AsyncSelect
                    placeholder="Select Employee"
                    v-model="contactProfileId"
                    :api-service="fetchContactProfiles"
                    :additional-query="{roles: 'employee', status: 'active'}"
                    label="name"
                    :reduce="name => name.id"
                />
              </div>
            </DateQuerySetter>
        </div>

        <div class="col-12">
          <div class="d-flex justify-content-end px-2">
            <button
                @click="exportTable"
                class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
            >
              Export
            </button>
          </div>
        </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card p-2" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead>
                <tr>
                  <th class="text-left">Employee Name</th>
                  <th class="text-left">Date | Day</th>
                  <th class="text-center">In Time</th>
                  <th class="text-center">Out Time</th>
                  <th class="text-center">Working Hours</th>
                  <th class="text-center">Overtime Hours</th>
                  <th class="text-center">Status</th>
                  <th class="text-center">Location</th>
                  <th class="text-center">Remarks</th>
                  <th class="text-center">Approval Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in tableItems" :key="i"
                    :class="getRowStatus(item.attendance_status)"
                >
                  <td>{{ item.contact_profile_name }}</td>
                  <td class="text-left">{{ item.calendar_date_formatted }}</td>
                  <td class="text-center">{{ item.in_time_formatted }}</td>
                  <td class="text-center">{{ item.out_time_formatted }}</td>
                  <td class="text-center">{{ item.working_hours_formatted }}</td>
                  <td class="text-center">{{ item.ot_hours_formatted }}</td>
                  <td class="text-center">{{ item.attendance_status }}</td>
                  <td class="text-center" width="20%">{{ item.attendance_locations }}</td>
                  <td class="text-center">{{ item.attendance_remarks }}</td>
                  <td class="text-center text-capitalize">{{ item.status }}</td>
                </tr>
                </tbody>
              </table>
              <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>

          <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
          </div>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { resetPaginationPage } from '@/services/utils/global';
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import handleContact from '@/services/modules/contact'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Pagination from '@/components/atom/Pagination'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const contactProfileId = ref('');
let contacts = ref([])
const tableItems = ref([]);
const params = ref({
    offset: 30,
});

const token = computed(() => localStorage.getItem('token'));

const {
  fetchAttendanceReport,
} = handleHrPayroll()
const { fetchContactProfiles } = handleContact()

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onClickToGo = () => {
   getAttendanceList();
}

const getRowStatus = (status) =>  {
  if (!status) return {};
  return {
    'bg-color': ['H', 'W', 'LT','L'].some(value => status.split('/').includes(value)),
    'bg-red': status === 'A',
  };
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getAttendanceList();
}

const getAttendanceList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchAttendanceReport(query).then(res => {
        if (!res.status){
            resetPaginationPage(store, route, router)
            showError(res.message)
            return
        }

        tableItems.value = res.data.data;
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (route.query.start) query += '&start_date=' + route.query.start;
    if (route.query.end) query += '&end_date=' + route.query.end;
    if (contactProfileId.value) query += '&contact_profile_id=' + contactProfileId.value
    query += '&offset=' + params.value.offset
    return query;
}

const exportTable = () => {
  let query = getQuery();
  let url = `${process.env.VUE_APP_BASE_URL}/api/hr-payroll/attendance-report${query}&_token=${token.value}&is_export=1&is_all=true`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

</script>

<style>
.bg-color{
  --bs-bg-opacity: 1;
  background-color: rgb(16 108 223 / 20%) !important;
}
.bg-red{
  --bs-bg-opacity: 1;
  background-color: rgba(223, 16, 95, 0.2) !important;
  background-color: rgb(165 45 90 / 12%) !important;
  color:red;
}
</style>